import { useEffect } from 'react';

/**
 * Tiny DOM-based SEO helper — updates document.title and common meta tags
 * imperatively. Avoids adding a new dependency (react-helmet has React 19
 * incompatibilities). Call once per page:
 *
 *   useSeo({
 *     title: 'Vendor · EntrepreNatives',
 *     description: '…',
 *     image: 'https://…/share.png',
 *     url: window.location.href,
 *     jsonLd: { "@context": "https://schema.org", … },
 *   });
 *
 * Values can be undefined to skip that field. On unmount, the previous title
 * is restored so SPA navigation stays consistent.
 */
const BRAND = 'EntrepreNatives';
const DEFAULT_IMG = '/og-image.png';
const DEFAULT_DESC = 'EntrepreNatives — the global marketplace for Indigenous-owned businesses. Discover verified Native American, First Nations, Alaska Native, Hawaiian, Métis & Inuit vendors.';

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, attrName] = selector.includes('property')
      ? ['property', selector.match(/property="([^"]+)"/)?.[1]]
      : ['name', selector.match(/name="([^"]+)"/)?.[1]];
    if (attrName) el.setAttribute(key, attrName);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

export function useSeo({ title, description, image, url, jsonLd, noindex = false } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const fullTitle = title
      ? (title.includes(BRAND) ? title : `${title} · ${BRAND}`)
      : `${BRAND} — Indigenous Marketplace`;
    document.title = fullTitle;

    const finalDesc = description || DEFAULT_DESC;
    const finalImage = image
      ? (image.startsWith('http') ? image : `${window.location.origin}${image}`)
      : `${window.location.origin}${DEFAULT_IMG}`;
    const finalUrl = url || window.location.href;

    setMeta('meta[name="description"]', 'content', finalDesc);
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', finalDesc);
    setMeta('meta[property="og:image"]', 'content', finalImage);
    setMeta('meta[property="og:url"]', 'content', finalUrl);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', finalDesc);
    setMeta('meta[name="twitter:image"]', 'content', finalImage);
    setLink('canonical', finalUrl);

    if (noindex) {
      setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    }

    upsertJsonLd('page-jsonld', jsonLd);

    return () => {
      document.title = prevTitle;
      // Keep the base index.html meta in place; don't revert — SPA pages
      // update these on next navigation anyway.
      upsertJsonLd('page-jsonld', null);
      if (noindex) {
        setMeta('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large');
      }
    };
  }, [title, description, image, url, JSON.stringify(jsonLd), noindex]);
}

export default useSeo;
